import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Youtube, Play, Eye, Search, RefreshCw, GraduationCap, Star, ShoppingCart, X, ExternalLink, Filter, ChevronDown } from 'lucide-react';
import WishlistStar from '../WishlistStar';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AcademyTab = () => {
  // Tab state
  const [activeSubTab, setActiveSubTab] = useState('free'); // 'free' or 'courses'
  
  // YouTube Videos state
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [videoSearchTerm, setVideoSearchTerm] = useState('');
  const [selectedVideoCategory, setSelectedVideoCategory] = useState('all');
  const [videoSortBy, setVideoSortBy] = useState('trending');
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Courses state
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [courseFilters, setCourseFilters] = useState({
    interest: [],
    skill_level: [],
    software: []
  });
  const [availableFilters, setAvailableFilters] = useState({
    interest: [],
    skill_level: [],
    software: []
  });
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loadingCourseDetails, setLoadingCourseDetails] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);

  // Load videos from API
  const loadVideos = useCallback(async () => {
    setLoadingVideos(true);
    try {
      const searchParam = videoSearchTerm ? `search=${encodeURIComponent(videoSearchTerm)}&` : '';
      const categoryParam = selectedVideoCategory !== 'all' ? `category=${selectedVideoCategory}&` : '';
      const cacheBuster = `t=${Date.now()}`;
      
      const response = await fetch(`${API}/api/videos?${searchParam}${categoryParam}limit=500&${cacheBuster}`);
      
      if (response.ok) {
        const data = await response.json();
        setVideos(data || []);
      }
    } catch (error) {
      console.error('Failed to load videos:', error);
    } finally {
      setLoadingVideos(false);
    }
  }, [videoSearchTerm, selectedVideoCategory]);

  // Load courses from API
  const loadCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      const response = await fetch(`${API}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      }
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  // Load course filters
  const loadCourseFilters = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/courses/filters`);
      if (response.ok) {
        const data = await response.json();
        setAvailableFilters(data);
      }
    } catch (error) {
      console.error('Failed to load course filters:', error);
    }
  }, []);

  // Load course details
  const loadCourseDetails = async (courseId) => {
    setLoadingCourseDetails(true);
    try {
      const response = await fetch(`${API}/api/courses/${courseId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedCourse(data);
        setShowCourseModal(true);
      }
    } catch (error) {
      console.error('Failed to load course details:', error);
    } finally {
      setLoadingCourseDetails(false);
    }
  };

  // Client-side sort for the video wall
  const sortedVideos = (() => {
    const arr = [...videos];
    switch (videoSortBy) {
      case 'newest': return arr.sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
      case 'most-viewed': return arr.sort((a, b) => (b.views || 0) - (a.views || 0));
      case 'most-liked': return arr.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      case 'a-z': return arr.sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
      case 'z-a': return arr.sort((a, b) => (b.title || '').localeCompare(a.title || '', undefined, { sensitivity: 'base' }));
      default: return arr;
    }
  })();

  // Video categories
  const getVideoCategories = () => [
    { id: 'all', name: 'All Videos' },
    { id: 'djing', name: "🎧 DJ'ing" },
    { id: 'gear', name: '🎛️ Gear & Equipment' },
    { id: 'live-sets', name: '🎵 Live Sets' },
    { id: 'mixing', name: '🎧 Mixing' },
    { id: 'obs', name: '🖥️ OBS' },
    { id: 'production', name: '🎹 Production' },
    { id: 'scratching', name: '💿 Scratching' },
    { id: 'streamelements', name: '⚡ Stream Elements' },
    { id: 'streaming', name: '📺 Streaming' },
    { id: 'studio-tours', name: '🏠 Studio Tours' },
    { id: 'tutorials', name: '📚 Tutorials' }
  ];

  // Track video view
  const handleVideoClick = (video) => {
    try {
      fetch(`${API}/api/videos/${video.id}/view`, { method: 'POST' })
        .catch(err => console.log('Failed to track view:', err));
    } catch (error) {
      console.log('Failed to track video view:', error);
    }
    setSelectedVideo(video);
    setShowYouTubeModal(true);
  };

  // Handle course card click - show preview
  const handleCoursePreview = (course) => {
    loadCourseDetails(course.id);
  };

  // Handle purchase click
  const handlePurchase = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Filter courses
  const getFilteredCourses = () => {
    return courses.filter(course => {
      // Search filter
      if (courseSearchTerm) {
        const searchLower = courseSearchTerm.toLowerCase();
        if (!course.title.toLowerCase().includes(searchLower) &&
            !course.course_type.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      
      // Interest filter
      if (courseFilters.interest.length > 0) {
        if (!courseFilters.interest.some(f => course.interest_tags?.includes(f))) {
          return false;
        }
      }
      
      // Skill filter
      if (courseFilters.skill_level.length > 0) {
        if (!courseFilters.skill_level.some(f => course.skill_tags?.includes(f))) {
          return false;
        }
      }
      
      // Software filter
      if (courseFilters.software.length > 0) {
        if (!courseFilters.software.some(f => course.software_tags?.includes(f))) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Toggle filter
  const toggleFilter = (category, value) => {
    setCourseFilters(prev => {
      const current = prev[category] || [];
      if (current.includes(value)) {
        return { ...prev, [category]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [category]: [...current, value] };
      }
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setCourseFilters({ interest: [], skill_level: [], software: [] });
    setCourseSearchTerm('');
  };

  // Format number
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  // Load data on mount and tab change
  useEffect(() => {
    if (activeSubTab === 'free') {
      loadVideos();
    } else {
      loadCourses();
      loadCourseFilters();
    }
  }, [activeSubTab, loadVideos, loadCourses, loadCourseFilters]);

  useEffect(() => {
    if (activeSubTab === 'free') {
      loadVideos();
    }
  }, [selectedVideoCategory, loadVideos, activeSubTab]);

  const videoCategories = getVideoCategories();
  const filteredCourses = getFilteredCourses();
  const activeFilterCount = courseFilters.interest.length + courseFilters.skill_level.length + courseFilters.software.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', maxHeight: '75vh' }}>
      {/* Sub-tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(0, 0, 0, 0.3)'
      }}>
        <button
          onClick={() => setActiveSubTab('free')}
          data-testid="free-videos-tab"
          style={{
            flex: 1,
            padding: '1rem 1.5rem',
            background: activeSubTab === 'free' ? 'rgba(0, 212, 255, 0.15)' : 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'free' ? '3px solid #00d4ff' : '3px solid transparent',
            color: activeSubTab === 'free' ? '#00d4ff' : 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s ease'
          }}
        >
          <Youtube size={20} />
          Free Videos
          <span style={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: '400' }}>Powered by YouTube</span>
        </button>
        <button
          onClick={() => setActiveSubTab('courses')}
          data-testid="pro-courses-tab"
          style={{
            flex: 1,
            padding: '1rem 1.5rem',
            background: activeSubTab === 'courses' ? 'rgba(255, 0, 255, 0.15)' : 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'courses' ? '3px solid #ff00ff' : '3px solid transparent',
            color: activeSubTab === 'courses' ? '#ff00ff' : 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s ease'
          }}
        >
          <GraduationCap size={20} />
          Pro Courses
        </button>
      </div>

      {/* FREE VIDEOS TAB */}
      {activeSubTab === 'free' && (
        <>
          {/* Toolbar */}
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            flexWrap: 'wrap',
            background: 'rgba(0, 0, 0, 0.2)',
            flexShrink: 0
          }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
              <Search style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '1rem',
                height: '1rem',
                color: 'rgba(255, 255, 255, 0.5)'
              }} />
              <input
                type="text"
                placeholder="Search videos, topics, or tags..."
                value={videoSearchTerm}
                onChange={(e) => setVideoSearchTerm(e.target.value)}
                data-testid="video-search-input"
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.5rem',
                  color: '#fff',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
                onKeyPress={(e) => { if (e.key === 'Enter') loadVideos(); }}
              />
            </div>

            {/* Category filter */}
            <select
              value={selectedVideoCategory}
              onChange={(e) => setSelectedVideoCategory(e.target.value)}
              data-testid="video-category-select"
              style={{
                padding: '0.75rem 2rem 0.75rem 1rem',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                color: '#fff',
                fontSize: '0.875rem',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {videoCategories.map(cat => (
                <option key={cat.id} value={cat.id} style={{ background: '#1a1a2e' }}>
                  {cat.name}
                </option>
              ))}
            </select>

            {/* Sort dropdown */}
            <select
              value={videoSortBy}
              onChange={(e) => setVideoSortBy(e.target.value)}
              data-testid="video-sort-select"
              style={{
                padding: '0.75rem 2rem 0.75rem 1rem',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                color: '#fff',
                fontSize: '0.875rem',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="trending" style={{ background: '#1a1a2e' }}>🔥 Trending</option>
              <option value="newest" style={{ background: '#1a1a2e' }}>📅 Newest</option>
              <option value="most-viewed" style={{ background: '#1a1a2e' }}>👁️ Most Viewed</option>
              <option value="most-liked" style={{ background: '#1a1a2e' }}>👍 Most Liked</option>
              <option value="a-z" style={{ background: '#1a1a2e' }}>🔤 A-Z</option>
              <option value="z-a" style={{ background: '#1a1a2e' }}>🔤 Z-A</option>
            </select>

            {/* Refresh button */}
            <button
              onClick={() => loadVideos()}
              disabled={loadingVideos}
              style={{
                background: 'transparent',
                border: 'none',
                color: loadingVideos ? 'rgba(255, 255, 255, 0.5)' : '#00d4ff',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: loadingVideos ? 'not-allowed' : 'pointer',
                padding: '0.5rem 0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <RefreshCw style={{ width: '0.875rem', height: '0.875rem', animation: loadingVideos ? 'spin 1s linear infinite' : 'none' }} />
              {loadingVideos ? 'refreshing...' : 'refresh'}
            </button>
          </div>

          {/* Results info */}
          <div style={{ padding: '0.75rem 1rem', background: 'rgba(0, 0, 0, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              {videoSearchTerm ? (
                `Found ${videos.length} video${videos.length !== 1 ? 's' : ''} for "${videoSearchTerm}"`
              ) : (
                `Showing ${videos.length} videos • Powered by YouTube`
              )}
            </p>
          </div>

          {/* Videos grid */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
            {loadingVideos ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading videos...</p>
              </div>
            ) : videos.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', textAlign: 'center' }}>
                <Youtube size={48} style={{ color: 'rgba(255, 255, 255, 0.3)', marginBottom: '1rem' }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fff', marginBottom: '0.5rem' }}>
                  No Videos Found
                </h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', maxWidth: '28rem' }}>
                  {videoSearchTerm ? `No videos match "${videoSearchTerm}". Try different keywords.` : 'No videos available in this category.'}
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '1rem'
              }}>
                {sortedVideos.map((video) => (
                  <div
                    key={video.id}
                    onClick={() => handleVideoClick(video)}
                    data-testid={`video-card-${video.id}`}
                    style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '0.75rem',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.5)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 212, 255, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Thumbnail */}
                    <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                      <img
                        src={video.thumbnail_url || video.thumbnailUrl || `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                        alt={video.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {/* Play overlay */}
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        padding: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Play size={32} style={{ color: '#00d4ff', fill: '#00d4ff' }} />
                      </div>
                      {/* Duration badge */}
                      {video.duration && video.duration !== '0:00' && (
                        <div style={{
                          position: 'absolute',
                          bottom: '0.5rem',
                          right: '0.5rem',
                          background: 'rgba(0, 0, 0, 0.8)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          color: '#fff',
                          fontWeight: '600'
                        }}>
                          {video.duration}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ padding: '1rem' }}>
                      {video.category && (
                        <div style={{
                          display: 'inline-block',
                          background: 'rgba(0, 212, 255, 0.15)',
                          color: '#00d4ff',
                          fontSize: '0.7rem',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '0.75rem',
                          fontWeight: '600',
                          marginBottom: '0.5rem',
                          border: '1px solid rgba(0, 212, 255, 0.3)'
                        }}>
                          {video.category}
                        </div>
                      )}
                      <h4 style={{
                        fontWeight: '600',
                        color: '#fff',
                        fontSize: '0.95rem',
                        marginBottom: '0.5rem',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {video.title}
                      </h4>
                      {video.channel_name && (
                        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                          {video.channel_name}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                        {video.views && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Eye size={14} />
                            {formatNumber(video.views)} views
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* PRO COURSES TAB */}
      {activeSubTab === 'courses' && (
        <>
          {/* Header */}
          <div style={{
            padding: '1rem 1.5rem',
            background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.1), rgba(150, 0, 100, 0.1))',
            borderBottom: '1px solid rgba(255, 0, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <img 
              src="https://cdn-gajed.nitrocdn.com/xnFRynGphiHZVmaFQUenqaOUuQKzUTfS/assets/images/optimized/rev-ee92653/cdn.digitaldjtips.com/app/uploads/2025/05/06192031/ddjt-logo-15-teaching-WHITE-300x53.png"
              alt="Digital DJ Tips"
              style={{ height: '28px', opacity: 0.9, flexShrink: 0 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', margin: 0 }}>
              Professional DJ courses from industry experts. Preview videos and purchase directly from Digital DJ Tips.
            </p>
          </div>

          {/* Toolbar */}
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            flexWrap: 'wrap',
            background: 'rgba(0, 0, 0, 0.2)'
          }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
              <Search style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '1rem',
                height: '1rem',
                color: '#ff00ff'
              }} />
              <input
                type="text"
                placeholder="Search courses..."
                value={courseSearchTerm}
                onChange={(e) => setCourseSearchTerm(e.target.value)}
                data-testid="course-search-input"
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  background: 'rgba(255, 0, 255, 0.1)',
                  border: '1px solid rgba(255, 0, 255, 0.3)',
                  borderRadius: '0.5rem',
                  color: '#fff',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
              />
            </div>

            {/* Filter button */}
            <button
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              data-testid="course-filters-btn"
              style={{
                padding: '0.75rem 1rem',
                background: showFiltersPanel ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 0, 255, 0.1)',
                border: '1px solid rgba(255, 0, 255, 0.3)',
                borderRadius: '0.5rem',
                color: '#ff00ff',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Filter size={16} />
              Filters
              {activeFilterCount > 0 && (
                <span style={{
                  background: '#ff00ff',
                  color: '#000',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: '700'
                }}>
                  {activeFilterCount}
                </span>
              )}
            </button>

            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Clear all
              </button>
            )}

            {/* Refresh */}
            <button
              onClick={() => loadCourses()}
              disabled={loadingCourses}
              style={{
                background: 'transparent',
                border: 'none',
                color: loadingCourses ? 'rgba(255, 255, 255, 0.5)' : '#ff00ff',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: loadingCourses ? 'not-allowed' : 'pointer',
                padding: '0.5rem 0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <RefreshCw style={{ width: '0.875rem', height: '0.875rem', animation: loadingCourses ? 'spin 1s linear infinite' : 'none' }} />
            </button>
          </div>

          {/* Filters Panel */}
          {showFiltersPanel && (
            <div style={{
              padding: '1rem',
              background: 'rgba(0, 0, 0, 0.3)',
              borderBottom: '1px solid rgba(255, 0, 255, 0.2)'
            }}>
              {/* Close bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid rgba(255, 0, 255, 0.15)'
              }}>
                <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>
                  Select filters below, close and refresh to apply
                </p>
                <button
                  onClick={() => setShowFiltersPanel(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ff00ff',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem',
                    textDecoration: 'underline'
                  }}
                >
                  Close Filters
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.5rem'
              }}>
                {/* Interest filters */}
                <div>
                  <h4 style={{ color: '#ff00ff', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                    I&apos;m interested in:
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {availableFilters.interest.map(filter => (
                      <label key={filter.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={courseFilters.interest.includes(filter.id)}
                          onChange={() => toggleFilter('interest', filter.id)}
                          style={{ accentColor: '#ff00ff' }}
                        />
                        <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>{filter.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Skill level filters */}
                <div>
                  <h4 style={{ color: '#ff00ff', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                    I want to:
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {availableFilters.skill_level.map(filter => (
                      <label key={filter.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={courseFilters.skill_level.includes(filter.id)}
                          onChange={() => toggleFilter('skill_level', filter.id)}
                          style={{ accentColor: '#ff00ff' }}
                        />
                        <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>{filter.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Software filters */}
                <div>
                  <h4 style={{ color: '#ff00ff', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                    My Software:
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {availableFilters.software.map(filter => (
                      <label key={filter.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={courseFilters.software.includes(filter.id)}
                          onChange={() => toggleFilter('software', filter.id)}
                          style={{ accentColor: '#ff00ff' }}
                        />
                        <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>{filter.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results info */}
          <div style={{ padding: '0.75rem 1rem', background: 'rgba(0, 0, 0, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              Showing {filteredCourses.length} of {courses.length} courses
            </p>
          </div>

          {/* Courses grid */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
            {loadingCourses ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading courses...</p>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', textAlign: 'center' }}>
                <GraduationCap size={48} style={{ color: 'rgba(255, 255, 255, 0.3)', marginBottom: '1rem' }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fff', marginBottom: '0.5rem' }}>
                  No Courses Found
                </h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', maxWidth: '28rem' }}>
                  Try adjusting your filters or search terms.
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    style={{
                      marginTop: '1rem',
                      padding: '0.5rem 1rem',
                      background: 'rgba(255, 0, 255, 0.2)',
                      border: '1px solid rgba(255, 0, 255, 0.4)',
                      borderRadius: '6px',
                      color: '#ff00ff',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '1.5rem'
              }}>
                {filteredCourses.map((course) => (
                  <div
                    key={course.id}
                    data-testid={`course-card-${course.id}`}
                    style={{
                      background: 'rgba(255, 0, 255, 0.05)',
                      border: '1px solid rgba(255, 0, 255, 0.2)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.6)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 0, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.2)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Thumbnail */}
                    <div style={{ position: 'relative', paddingTop: '56.25%', background: '#0a1929' }}>
                      <img
                        src={course.image}
                        alt={course.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225'%3E%3Crect fill='%230a1929' width='400' height='225'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%2300d4ff'%3EDigital DJ Tips%3C/text%3E%3C/svg%3E";
                        }}
                      />
                      {/* Course type badge */}
                      <div style={{
                        position: 'absolute',
                        top: '0.75rem',
                        left: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.8)',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        color: '#ff00ff',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {course.course_type}
                      </div>
                    </div>

                    {/* Content */}
                    <div style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <h4 style={{
                          fontWeight: '600',
                          color: '#fff',
                          fontSize: '1rem',
                          flex: 1,
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          margin: 0
                        }}>
                          {course.title}
                        </h4>
                        <WishlistStar 
                          itemType="course"
                          itemData={{
                            id: course.id,
                            title: course.title,
                            artist: course.instructor || 'Digital DJ Tips',
                            url: course.url,
                            image: course.image,
                            course_type: course.course_type
                          }}
                          size={16}
                        />
                      </div>

                      {/* Tags preview */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '1rem' }}>
                        {course.software_tags?.slice(0, 3).map(tag => (
                          <span key={tag} style={{
                            background: 'rgba(255, 0, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.65rem',
                            padding: '0.15rem 0.4rem',
                            borderRadius: '3px'
                          }}>
                            {tag.replace(/-/g, ' ')}
                          </span>
                        ))}
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleCoursePreview(course)}
                          data-testid={`preview-btn-${course.id}`}
                          style={{
                            flex: 1,
                            padding: '0.6rem',
                            background: 'rgba(255, 0, 255, 0.15)',
                            border: '1px solid rgba(255, 0, 255, 0.4)',
                            borderRadius: '6px',
                            color: '#ff00ff',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.4rem',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <Play size={14} />
                          Preview
                        </button>
                        <button
                          onClick={() => handlePurchase(course.url)}
                          data-testid={`purchase-btn-${course.id}`}
                          style={{
                            flex: 1,
                            padding: '0.6rem',
                            background: 'linear-gradient(135deg, #ff00ff, #cc00cc)',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#fff',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.4rem',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <ShoppingCart size={14} />
                          Purchase
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* YouTube Video Modal */}
      {showYouTubeModal && selectedVideo && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002
          }}
          onClick={() => { setShowYouTubeModal(false); setSelectedVideo(null); }}
        >
          <div 
            style={{
              width: '90%',
              maxWidth: '900px',
              background: 'rgba(26, 26, 46, 0.95)',
              borderRadius: '16px',
              padding: '1rem',
              overflow: 'hidden',
              border: '3px solid #00d4ff'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                margin: 0,
                color: '#00d4ff'
              }}>
                BeatSeek Academy
              </h3>
              <button
                onClick={() => { setShowYouTubeModal(false); setSelectedVideo(null); }}
                style={{
                  background: 'rgba(0, 212, 255, 0.2)',
                  border: '1px solid rgba(0, 212, 255, 0.4)',
                  color: '#00d4ff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Video Info */}
            <div style={{ marginBottom: '0.75rem' }}>
              <p style={{ fontSize: '1rem', fontWeight: '600', color: '#fff', margin: '0 0 0.25rem 0' }}>
                {selectedVideo.title}
              </p>
              {selectedVideo.channel_name && (
                <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                  {selectedVideo.channel_name}
                </p>
              )}
            </div>

            {/* Video Player */}
            <div style={{ 
              position: 'relative', 
              width: '100%',
              paddingTop: '56.25%',
              background: '#000',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1&controls=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </div>

            {/* Footer */}
            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>
                Powered by YouTube • Free DJ Education
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Course Preview Modal */}
      {showCourseModal && selectedCourse && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002
          }}
          onClick={() => { setShowCourseModal(false); setSelectedCourse(null); }}
        >
          <div 
            style={{
              width: '90%',
              maxWidth: '900px',
              maxHeight: '90vh',
              background: 'rgba(26, 26, 46, 0.98)',
              borderRadius: '16px',
              padding: '1.5rem',
              overflow: 'auto',
              border: '3px solid #ff00ff',
              boxShadow: '0 0 60px rgba(255, 0, 255, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid rgba(255, 0, 255, 0.2)'
            }}>
              <div>
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(255, 0, 255, 0.15)',
                  color: '#ff00ff',
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  textTransform: 'uppercase'
                }}>
                  {selectedCourse.course_type}
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  margin: 0,
                  color: '#fff',
                  lineHeight: '1.3'
                }}>
                  {selectedCourse.title}
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0.5rem 0 0 0' }}>
                  by Digital DJ Tips
                </p>
              </div>
              <button
                onClick={() => { setShowCourseModal(false); setSelectedCourse(null); }}
                style={{
                  background: 'rgba(255, 0, 255, 0.2)',
                  border: '1px solid rgba(255, 0, 255, 0.4)',
                  color: '#ff00ff',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Video Preview - Show message about domain restriction */}
            <div style={{
              width: '100%',
              paddingTop: '56.25%',
              background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.1), rgba(100, 0, 100, 0.2))',
              borderRadius: '12px',
              position: 'relative',
              marginBottom: '1.5rem',
              border: '1px solid rgba(255, 0, 255, 0.2)'
            }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                padding: '2rem'
              }}>
                <Play size={56} style={{ color: '#ff00ff', marginBottom: '1rem', opacity: 0.8 }} />
                <h4 style={{ color: '#fff', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                  Preview Available on Digital DJ Tips
                </h4>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem', marginBottom: '1rem', maxWidth: '400px' }}>
                  Course previews are hosted exclusively on Digital DJ Tips. Click below to watch the full preview and learn more about this course.
                </p>
                <button
                  onClick={() => handlePurchase(selectedCourse.url)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(255, 0, 255, 0.2)',
                    border: '1px solid rgba(255, 0, 255, 0.5)',
                    borderRadius: '8px',
                    color: '#ff00ff',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Play size={18} />
                  Watch Preview on Digital DJ Tips
                  <ExternalLink size={14} />
                </button>
              </div>
            </div>

            {/* Price & Purchase */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              background: 'rgba(255, 0, 255, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 0, 255, 0.2)'
            }}>
              <div>
                {selectedCourse.price && (
                  <p style={{ fontSize: '2rem', fontWeight: '700', color: '#ff00ff', margin: 0 }}>
                    {selectedCourse.price}
                  </p>
                )}
                <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)', margin: '0.25rem 0 0 0' }}>
                  One-time purchase • Lifetime access
                </p>
              </div>
              <button
                onClick={() => handlePurchase(selectedCourse.url)}
                data-testid="modal-purchase-btn"
                style={{
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, #ff00ff, #cc00cc)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 0, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <ShoppingCart size={20} />
                Purchase on Digital DJ Tips
                <ExternalLink size={16} />
              </button>
            </div>

            {/* Footer */}
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.4)', margin: 0 }}>
                You will be redirected to Digital DJ Tips to complete your purchase
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Loading overlay for course details */}
      {loadingCourseDetails && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10003
        }}>
          <div style={{ textAlign: 'center', color: '#ff00ff' }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '1rem' }}>Loading course details...</p>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AcademyTab;
